/* eslint-disable global-require */
import { useState, React } from "react";
import { Importer, ImporterField } from "react-csv-importer";
import {
  Paper,
  Typography,
  Divider,
  CircularProgress,
  Snackbar,
  LinearProgress,
  Box,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import moment from "moment";
import useSupabaseFileUpload from "../../../../../hooks/useSupabaseFileUpload";
import { useCommissionContext } from "../../../../../contexts/CommissionContext";
import commissionService from "../../../../../services/commissions.service";

/**
 * AgentImporter component for importing agent commission data from CSV
 * @returns {React.ReactElement} The agent importer component
 */
export default function AgentImporter() {
  const [importArray, setImportArray] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [processingStage, setProcessingStage] = useState("");
  const [localSnackbar, setLocalSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  // Get context functions
  const { showSnackbar } = useCommissionContext();

  // Temporary array for collecting CSV data
  const tempArray = [];

  // Show local snackbar
  const showLocalSnackbar = (message, severity = "info") => {
    setLocalSnackbar({ open: true, message, severity });
  };

  // Close local snackbar
  const handleCloseSnackbar = () => {
    setLocalSnackbar(prev => ({ ...prev, open: false }));
  };

  /**
   * Handles the agent import process
   * @param {Array} data - The data to import
   */
  const handleAgentImport = async data => {
    try {
      setIsLoading(true);
      setProcessingStage("preparing");
      setUploadProgress(10);

      // Convert data to CSV format
      const Papa = require("papaparse");
      const unparsed = Papa.unparse(data);
      const blob = new Blob([unparsed], { type: "text/csv" });

      // Generate filename with timestamp for uniqueness
      const filename = `agent_commissions_${moment().format("YYYYMMDDHHmmss")}`;

      setProcessingStage("uploading");
      setUploadProgress(30);

      // Upload file to Supabase storage
      const uploadResult = await useSupabaseFileUpload(
        filename,
        blob,
        "CommissionsDatestamps" // bucket name
      );

      if (uploadResult.error) {
        throw new Error(`Error uploading file: ${uploadResult.error.message}`);
      }

      setProcessingStage("processing");
      setUploadProgress(60);

      // Trigger import in the backend
      const importResult = await commissionService.importCommissions({
        type: "Agent",
        filePath: filename,
      });

      setUploadProgress(90);

      if (!importResult.success) {
        throw new Error(importResult.error || "Error importing data");
      }

      setUploadProgress(100);
      setProcessingStage("complete");

      // Show success message in both local and global snackbars
      showLocalSnackbar(
        "Agent commission data imported successfully",
        "success"
      );
      showSnackbar("Agent commission data imported successfully", "success");
    } catch (error) {
      console.error("Import error:", error);
      showLocalSnackbar(`Import failed: ${error.message}`, "error");
      showSnackbar(`Agent import failed: ${error.message}`, "error");
    } finally {
      // Keep loading state a bit longer for feedback when complete
      setTimeout(() => {
        setIsLoading(false);
        setUploadProgress(0);
        setProcessingStage("");
      }, 1500);
    }
  };

  return (
    <Paper elevation={2} style={{ padding: 16 }}>
      <Typography variant="h6" gutterBottom>
        Agent Commission Import
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Upload a CSV file with agent commission data
      </Typography>
      <Divider style={{ marginBottom: 16 }} />

      {isLoading ? (
        <Box p={2}>
          <Typography variant="body2" gutterBottom>
            {processingStage === "preparing" && "Preparing data..."}
            {processingStage === "uploading" && "Uploading file..."}
            {processingStage === "processing" &&
              "Processing commission data..."}
            {processingStage === "complete" && "Import completed successfully!"}
          </Typography>

          <LinearProgress
            variant="determinate"
            value={uploadProgress}
            style={{ height: 8, borderRadius: 4, marginBottom: 16 }}
          />

          <Box display="flex" justifyContent="center">
            <CircularProgress size={32} />
          </Box>
        </Box>
      ) : (
        <Importer
          restartable
          processChunk={async rows => {
            rows.forEach(row => {
              tempArray.push({
                order_number: row.order_number,
                agent_activation_comm_paid_date_1: row.agent_payment_date,
                agent_activation_comm_paid_to_1: row.agent_act_comm_paid_to_1,
                agent_activation_comm_amount_paid_1:
                  row.agent_act_comm_amount_1,
                agent_activation_comm_tier_1: row.agent_act_comm_tier_1,
              });
            });
            setImportArray(tempArray);
          }}
          onComplete={async () => {
            await handleAgentImport(importArray);
          }}
        >
          {/* Required Fields */}
          <ImporterField name="order_number" label="Order Number" />
          <ImporterField name="agent_payment_date" label="Agent Payment Date" />
          <ImporterField
            name="agent_act_comm_paid_to_1"
            label="Agent Activation Commission Paid To 1"
          />
          <ImporterField
            name="agent_act_comm_amount_1"
            label="Agent Activation Commission Amount 1"
          />
          <ImporterField
            name="agent_act_comm_tier_1"
            label="Agent Activation Commission Tier 1"
          />
        </Importer>
      )}

      <Snackbar
        open={localSnackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={localSnackbar.severity}>
          {localSnackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
}
